import { useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  ContactImportReportListResponse,
  ContactImportReportPageSize,
  OrganizationMemberRoleName,
} from "@multi-chat/shared";
import { listContactImportReports } from "../../api/reports-api.js";
import {
  clearContactImportFilters,
  contactImportReportHref,
  hasActiveContactImportFilters,
  parseContactImportReportQuery,
  type ContactImportReportQuery,
} from "./contact-import-report-query.js";

const SEARCH_DEBOUNCE_MS = 280;

export function useContactImportReport(input: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): {
  query: ContactImportReportQuery;
  list: ContactImportReportListResponse | null;
  loading: boolean;
  error: unknown;
  filtersActive: boolean;
  canAccess: boolean;
  dateFrom: string;
  dateTo: string;
  setSearch: (search: string) => void;
  setDateRange: (from: string, to: string) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: ContactImportReportPageSize) => void;
  resetFilters: () => void;
  refresh: () => void;
} {
  const canAccess = input.role === "OWNER" || input.role === "ADMIN";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const query = useMemo(
    () => parseContactImportReportQuery(searchParamsKey),
    [searchParamsKey],
  );
  const [searchDraft, setSearchDraft] = useState(query.search);
  const [debouncedSearch, setDebouncedSearch] = useState(query.search);
  const [list, setList] = useState<ContactImportReportListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setSearchDraft(query.search);
    setDebouncedSearch(query.search);
  }, [query.search]);

  const syncSearchToUrl = useEffectEvent((nextSearch: string) => {
    if (nextSearch === query.search) return;
    navigate(contactImportReportHref({ ...query, search: nextSearch, page: 1 }), { replace: true });
  });

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchDraft.trim().slice(0, 200);
      setDebouncedSearch(next);
      syncSearchToUrl(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchDraft]);

  const replaceQuery = useCallback(
    (next: ContactImportReportQuery) => {
      navigate(contactImportReportHref(next), { replace: true });
    },
    [navigate],
  );

  const load = useEffectEvent(async (signal: AbortSignal) => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    try {
      const next = await listContactImportReports(
        input.organizationId,
        {
          page: query.page,
          pageSize: query.pageSize,
          search: debouncedSearch || undefined,
          fromDate: query.from,
          toDate: query.to,
        },
        signal,
      );
      if (!signal.aborted) setList(next);
    } catch (caught) {
      if (signal.aborted) return;
      setError(caught);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  });

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [
    input.organizationId,
    query.page,
    query.pageSize,
    query.from,
    query.to,
    debouncedSearch,
    refreshToken,
    canAccess,
  ]);

  const visibleQuery: ContactImportReportQuery = { ...query, search: searchDraft };

  return {
    query: visibleQuery,
    list,
    loading,
    error,
    filtersActive: hasActiveContactImportFilters(visibleQuery),
    canAccess,
    dateFrom: query.from ?? "",
    dateTo: query.to ?? "",
    setSearch: setSearchDraft,
    setDateRange: (from, to) => {
      if (!from || !to) {
        const next: ContactImportReportQuery = { ...query, search: searchDraft, page: 1 };
        delete next.from;
        delete next.to;
        replaceQuery(next);
        return;
      }
      replaceQuery({ ...query, search: searchDraft, from, to, page: 1 });
    },
    setPage: (page) => replaceQuery({ ...query, search: searchDraft, page }),
    setPageSize: (pageSize) => replaceQuery({ ...query, search: searchDraft, pageSize, page: 1 }),
    resetFilters: () => {
      setSearchDraft("");
      setDebouncedSearch("");
      replaceQuery(clearContactImportFilters(query));
    },
    refresh: () => setRefreshToken((value) => value + 1),
  };
}
